import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, ArrowRight, Loader2, ArrowLeft, RefreshCw, ExternalLink, User as UserIcon, Phone as PhoneIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/user-auth`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_61ca5ff4-bcc2-4cdc-8453-7407936ace18/artifacts/6nklllpo_TaxSetu_Only_Logo_Design_Infinite-removebg-preview.png";

const UserPortalLoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // honour ?redirect=<path> so simulator deep-links bring user back after login
  const redirectAfterLogin = (() => {
    try {
      const sp = new URLSearchParams(location.search);
      const r = sp.get('redirect');
      // safety: only allow same-origin paths (must start with '/')
      if (r && r.startsWith('/')) return r;
    } catch (_) { /* ignore */ }
    return '/portal';
  })();
  // 'form' (email-only or email+details) | 'otp'
  const [step, setStep] = useState('form');
  const [tab, setTab] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const sendOtp = async () => {
    const res = await fetch(`${API_BASE}/send-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Failed to send OTP');
    return data;
  };

  const checkEmailExists = async () => {
    const res = await fetch(`${API_BASE}/check-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim().toLowerCase() }),
    });
    const data = await res.json();
    return !!data.exists;
  };

  const handleContinue = async (e) => {
    e?.preventDefault();
    if (!isValidEmail(email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (tab === 'signup' && !name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    setLoading(true);
    try {
      const exists = await checkEmailExists();

      if (tab === 'signin' && !exists) {
        // No account — gently switch to Sign Up
        setTab('signup');
        toast('No account found for this email. Please complete a quick sign-up.', { icon: 'ℹ️' });
        setLoading(false);
        return;
      }
      if (tab === 'signup' && exists) {
        // Already exists — sign them in
        toast('You already have an account. Sending sign-in OTP…');
      }

      if (tab === 'signup') {
        localStorage.setItem('practiq_pending_signup', JSON.stringify({ name: name.trim(), phone: phone.trim() }));
      }

      await sendOtp();
      toast.success('OTP sent! Check your inbox (and spam folder)');
      setStep('otp');
    } catch (err) {
      toast.error(err.message || 'Could not continue. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 6) {
      toast.error('Please enter the 6-digit OTP');
      return;
    }
    setLoading(true);
    try {
      // Step 3: verify OTP — pass name+phone if signing up so backend stores them
      const pending = localStorage.getItem('practiq_pending_signup');
      let signupData = {};
      try { signupData = pending ? JSON.parse(pending) : {}; } catch { /* ignore */ }
      const verifyBody = {
        email: email.trim().toLowerCase(),
        otp: otp.trim(),
        ...(signupData.name ? { name: signupData.name } : {}),
        ...(signupData.phone ? { phone: signupData.phone } : {}),
      };
      const res = await fetch(`${API_BASE}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifyBody),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');

      localStorage.setItem('practiq_portal_token', data.token);
      localStorage.setItem('practiq_portal_email', data.email);
      // Prefer server-returned name (covers founder override + saved sign-up name)
      if (data.name) {
        localStorage.setItem('practiq_portal_name', data.name);
      } else if (signupData.name) {
        localStorage.setItem('practiq_portal_name', signupData.name);
      }
      if (signupData.phone) {
        localStorage.setItem('practiq_portal_phone', signupData.phone);
      }
      localStorage.removeItem('practiq_pending_signup');

      toast.success(tab === 'signup' ? 'Welcome to PractiQ!' : 'Welcome back!');
      navigate(redirectAfterLogin);
    } catch (err) {
      toast.error(err.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try { await sendOtp(); toast.success('OTP resent'); }
    catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  };

  const switchTab = (newTab) => {
    if (loading) return;
    setTab(newTab);
    setStep('form');
    setOtp('');
  };

  return (
    <div className="fixed inset-0 bg-[#0A0F1C] overflow-y-auto" data-testid="portal-login-page">
      {/* Top brand bar */}
      <div className="px-6 py-5 flex items-center justify-between border-b border-white/5">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl blur-lg opacity-30"></div>
            <img src={LOGO_URL} alt="PractiQ" className="w-10 h-10 object-contain relative z-10" />
          </div>
          <div className="flex items-baseline gap-2">
            <h1 className="text-xl font-semibold">
              <span className="bg-gradient-to-r from-teal-400 to-cyan-400 bg-clip-text text-transparent">PractiQ</span>
            </h1>
            <a
              href="https://www.taxsetu.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-teal-400 transition-colors inline-flex items-center gap-1"
            >
              by <span className="text-gray-400">TaxSetu AI</span>
              <ExternalLink className="w-2.5 h-2.5 opacity-60" />
            </a>
          </div>
        </div>
        <a
          href="/"
          className="text-sm text-gray-500 hover:text-teal-400 inline-flex items-center gap-1.5 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </a>
      </div>

      {/* Centre card */}
      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-160px)]">
        <div className="w-full max-w-md">
          <div className="bg-[#0F1626] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {step === 'form' && (
              <>
                {/* Tabs */}
                <div className="grid grid-cols-2 border-b border-white/10" data-testid="auth-tabs">
                  <button
                    type="button"
                    onClick={() => switchTab('signin')}
                    className={`py-4 text-sm font-semibold tracking-wide transition-all relative ${
                      tab === 'signin' ? 'text-white bg-white/[0.03]' : 'text-gray-500 hover:text-gray-300'
                    }`}
                    data-testid="tab-signin"
                  >
                    Sign In
                    {tab === 'signin' && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full"></span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => switchTab('signup')}
                    className={`py-4 text-sm font-semibold tracking-wide transition-all relative ${
                      tab === 'signup' ? 'text-white bg-white/[0.03]' : 'text-gray-500 hover:text-gray-300'
                    }`}
                    data-testid="tab-signup"
                  >
                    Sign Up
                    {tab === 'signup' && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[2px] bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full"></span>
                    )}
                  </button>
                </div>

                {/* Form body */}
                <div className="p-7">
                  <h2 className="text-2xl font-bold text-white mb-1" data-testid="form-heading">
                    {tab === 'signin' ? 'Welcome back' : 'Create your account'}
                  </h2>
                  <p className="text-sm text-gray-500 mb-6">
                    {tab === 'signin'
                      ? 'Sign in to access your simulators and purchases.'
                      : 'Join PractiQ in under 30 seconds.'}
                  </p>

                  <form onSubmit={handleContinue} className="space-y-4" data-testid={tab === 'signin' ? 'signin-form' : 'signup-form'}>
                    {tab === 'signup' && (
                      <div>
                        <Label htmlFor="name" className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider">
                          Full Name <span className="text-red-400">*</span>
                        </Label>
                        <div className="relative">
                          <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <Input
                            id="name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Shivangi Mittal"
                            className="bg-[#0A0F1C] border-white/10 text-white pl-10 h-11"
                            disabled={loading}
                            autoFocus
                            data-testid="signup-name-input"
                          />
                        </div>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="email" className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider">
                        Email <span className="text-red-400">*</span>
                      </Label>
                      <div className="relative">
                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                        <Input
                          id="email"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@example.com"
                          className="bg-[#0A0F1C] border-white/10 text-white pl-10 h-11"
                          disabled={loading}
                          autoFocus={tab === 'signin'}
                          data-testid="email-input"
                        />
                      </div>
                    </div>

                    {tab === 'signup' && (
                      <div>
                        <Label htmlFor="phone" className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider">
                          Phone <span className="text-gray-600 normal-case">(optional)</span>
                        </Label>
                        <div className="relative">
                          <PhoneIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/[^0-9+\s-]/g, ''))}
                            placeholder="+91 98XXXXXXXX"
                            className="bg-[#0A0F1C] border-white/10 text-white pl-10 h-11"
                            disabled={loading}
                            maxLength={15}
                            data-testid="signup-phone-input"
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      type="submit"
                      disabled={loading || !isValidEmail(email) || (tab === 'signup' && !name.trim())}
                      className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium mt-2"
                      data-testid="continue-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      {tab === 'signin' ? 'Send sign-in code' : 'Create account'}
                      {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                    </Button>
                  </form>

                  <p className="text-center text-xs text-gray-500 mt-5">
                    {tab === 'signin' ? (
                      <>New to PractiQ?{' '}
                        <button type="button" onClick={() => switchTab('signup')} className="text-teal-400 hover:underline font-medium" data-testid="switch-to-signup">
                          Create an account
                        </button>
                      </>
                    ) : (
                      <>Already have an account?{' '}
                        <button type="button" onClick={() => switchTab('signin')} className="text-teal-400 hover:underline font-medium" data-testid="switch-to-signin">
                          Sign in
                        </button>
                      </>
                    )}
                  </p>
                </div>
              </>
            )}

            {step === 'otp' && (
              <div className="p-7" data-testid="otp-step">
                <h2 className="text-2xl font-bold text-white mb-1">Verify your email</h2>
                <p className="text-sm text-gray-500 mb-6">
                  We sent a 6-digit code to <span className="text-gray-300 font-medium">{email}</span>
                </p>

                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <Label htmlFor="otp" className="text-xs text-gray-400 mb-1.5 block uppercase tracking-wider">
                      6-digit code
                    </Label>
                    <Input
                      id="otp"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={6}
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="bg-[#0A0F1C] border-white/10 text-white text-center text-2xl tracking-[0.5em] font-mono h-14"
                      autoFocus
                      disabled={loading}
                      data-testid="otp-input"
                    />
                    <div className="flex justify-between items-center mt-2">
                      <button
                        type="button"
                        onClick={() => { setStep('form'); setOtp(''); }}
                        className="text-[11px] text-gray-500 hover:text-teal-400 inline-flex items-center gap-1"
                        data-testid="change-email-btn"
                      >
                        <ArrowLeft className="w-3 h-3" /> Change email
                      </button>
                      <button
                        type="button"
                        onClick={handleResendOtp}
                        disabled={loading}
                        className="text-[11px] text-gray-500 hover:text-teal-400 inline-flex items-center gap-1"
                        data-testid="resend-otp-btn"
                      >
                        <RefreshCw className="w-3 h-3" /> Resend
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full h-11 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-medium"
                    data-testid="verify-otp-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {tab === 'signup' ? 'Create account' : 'Sign in'}
                    {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                  </Button>
                </form>
              </div>
            )}
          </div>

          <p className="text-center text-[11px] text-gray-600 mt-6">
            By continuing you agree to PractiQ&apos;s{' '}
            <a href="/terms" className="text-gray-500 hover:text-teal-400">Terms</a> &{' '}
            <a href="/privacy-policy" className="text-gray-500 hover:text-teal-400">Privacy Policy</a>.
          </p>
        </div>
      </div>

      {/* Bottom brand strip */}
      <div className="px-6 py-4 border-t border-white/5 text-center text-[11px] text-gray-700">
        PractiQ LLP · A TaxSetu AI Product ·{' '}
        <a href="https://www.taxsetu.ai" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-teal-400 transition-colors">
          www.taxsetu.ai
        </a>
      </div>
    </div>
  );
};

export default UserPortalLoginPage;
